import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AuditTrailRow } from '@/application/metrics';
import { persistReportScope } from '@/application/metrics/metricsScope';
import { getAuditTrail } from '@/infrastructure/http/metricsApi';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { DomainPageHeader } from '@/presentation/components/domain/DomainPageHeader';
import { DomainScopeBar } from '@/presentation/components/domain/DomainScopeBar';
import { DomainTabs } from '@/presentation/components/domain/DomainTabs';
import { DataQualityBlock } from '@/presentation/components/metrics/DataQualityBlock';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';

type OpsTab = 'quality' | 'audit';

/**
 * Dominio secundario: calidad de datos + auditoría de accesos.
 * Saca estas vistas del catálogo de reportes hacia “Más”.
 */
export function OperationsPage() {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const [searchParams, setSearchParams] = useSearchParams();
  const { period, year, month, label: periodLabel } = useGlobalPeriod();
  const locale = i18n.language;

  const tenantIds = useMemo(
    () => user?.scope.allowedTenantIds ?? [],
    [user?.scope.allowedTenantIds]
  );
  const clinicOptions = useMemo(
    () => tenantIds.map((id) => ({ value: id, label: clinicNames[id] ?? id })),
    [tenantIds, clinicNames]
  );

  const tenantFromQuery = searchParams.get('tenant') ?? '';
  const tabParam = searchParams.get('tab');

  const canQuality = hasPermission('metrics.calidad');
  const canAudit = hasPermission('reports.view') || hasPermission('reports.export');
  const canView = canQuality || canAudit;

  const availableTabs = useMemo(() => {
    const items: Array<{ id: OpsTab; label: string }> = [];
    if (canQuality) items.push({ id: 'quality', label: t('domain.operations.tabs.quality') });
    if (canAudit) items.push({ id: 'audit', label: t('domain.operations.tabs.audit') });
    return items;
  }, [canQuality, canAudit, t]);

  const requestedTab: OpsTab = tabParam === 'audit' ? 'audit' : 'quality';
  const activeTab: OpsTab = availableTabs.some((item) => item.id === requestedTab)
    ? requestedTab
    : (availableTabs[0]?.id ?? 'quality');

  const [clinicId, setClinicId] = useState(
    () => (tenantFromQuery && tenantIds.includes(tenantFromQuery) ? tenantFromQuery : tenantIds[0] ?? '')
  );
  const [auditRows, setAuditRows] = useState<AuditTrailRow[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantFromQuery && tenantIds.includes(tenantFromQuery)) {
      setClinicId(tenantFromQuery);
    }
  }, [tenantFromQuery, tenantIds]);

  useEffect(() => {
    if (!clinicId && clinicOptions[0]?.value) {
      setClinicId(clinicOptions[0].value);
    }
  }, [clinicId, clinicOptions]);

  const setActiveTab = (next: string) => {
    const value: OpsTab = next === 'audit' ? 'audit' : 'quality';
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value === 'quality') params.delete('tab');
        else params.set('tab', value);
        return params;
      },
      { replace: true }
    );
  };

  const handleClinicChange = (id: string) => {
    setClinicId(id);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tenant', id);
        return params;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (clinicId) {
      persistReportScope(period, clinicId);
    }
  }, [clinicId, period]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !canAudit || activeTab !== 'audit') {
      return;
    }
    void (async () => {
      setIsLoadingAudit(true);
      setAuditError(null);
      try {
        const rows = await getAuditTrail(token, { limit: 200 });
        if (!cancelled) setAuditRows(rows);
      } catch (err) {
        if (!cancelled) {
          setAuditRows([]);
          setAuditError(err instanceof Error ? err.message : t('metrics.states.error.message'));
        }
      } finally {
        if (!cancelled) setIsLoadingAudit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, canAudit, activeTab, t]);

  if (!canView) {
    return <AccessDenied homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  return (
    <div className="panel-page panel-safe-bottom bi-surface space-y-5">
      <DomainPageHeader
        question={t('domain.operations.question')}
        title={t('domain.operations.title')}
        subtitle={t('domain.operations.subtitle', {
          period: periodLabel,
        })}
      />

      <DomainScopeBar
        showClinic={activeTab === 'quality'}
        clinicId={clinicId}
        clinicOptions={clinicOptions}
        onClinicChange={handleClinicChange}
      />

      {availableTabs.length > 1 && (
        <DomainTabs
          tabs={availableTabs}
          active={activeTab}
          onChange={setActiveTab}
          ariaLabel={t('domain.operations.tabsLabel')}
        />
      )}

      {activeTab === 'quality' && canQuality && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('domain.operations.qualityHint')}</p>
          {clinicId ? (
            <DataQualityBlock tenantId={clinicId} year={year} month={month} />
          ) : (
            <p className="text-sm text-gray-500">{t('domain.operations.noClinic')}</p>
          )}
        </div>
      )}

      {activeTab === 'audit' && canAudit && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('domain.operations.auditHint')}</p>
          {isLoadingAudit && <LoadingBlock minHeightClassName="min-h-[6rem]" />}
          {auditError && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-300">
              {auditError}
            </p>
          )}
          {!isLoadingAudit && !auditError && auditRows.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.pages.auditEmpty')}</p>
          )}
          {auditRows.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[var(--bi-line)] bg-white/80 dark:bg-white/5">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-[var(--bi-line)] text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2 font-semibold">{t('reports.pages.auditWhen')}</th>
                    <th className="px-3 py-2 font-semibold">{t('reports.pages.auditWho')}</th>
                    <th className="px-3 py-2 font-semibold">{t('reports.pages.auditRole')}</th>
                    <th className="px-3 py-2 font-semibold">{t('reports.pages.auditAction')}</th>
                    <th className="px-3 py-2 font-semibold">{t('reports.pages.auditDetail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.map((row) => (
                    <tr key={row.id} className="border-b border-[var(--bi-line)] last:border-0">
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-700 dark:text-gray-200">
                        {new Date(row.at).toLocaleString(locale)}
                      </td>
                      <td className="px-3 py-2">{row.usuarioEmail ?? '—'}</td>
                      <td className="px-3 py-2">{row.rol ?? '—'}</td>
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{row.concepto}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{row.detalle ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
